import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {OrderStore, LocationStore} from '../../store';
import {DateTimeService, IBaseResponse, WebService} from '../../services';
import dayjs, {Dayjs} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {Vue} from 'vue-property-decorator';
import type {
	ILocation,
	ILocationHours,
	IShift,
	TMaybeLocation,
} from '../../store';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface IAvailability {
	/**
	 * key in the form <token>-<numerical day of week>
	 *
	 * Currently, the token is hardcoded as "default" in the dayKey method.
	 * Numbers for days of the week come from Day.js's format "d" output
	 * (Sunday is 0, etc.).
	 */
	dayId: string;
	date: Dayjs;
	shifts: IShift[];
}

interface INextAvailableDate {
	date: Dayjs;
	isInActiveShift: boolean;
}


@Module({name: 'locationHours', namespaced: true})
class LocationHoursModule extends VuexModule {
	locationHoursInternal: Record<ILocation['id'], ILocationHours> = {};

	get location(): TMaybeLocation {
		return LocationStore.location(OrderStore.selectedLocationId);
	}

	get locationHours() {
		return (locationId: string | null = null) => {
			if(!locationId) {
				locationId = OrderStore.selectedLocationId;
			}
			if(locationId === null || !this.locationHoursInternal[locationId]) {
				return null;
			} else {
				return this.locationHoursInternal[locationId];
			}
		};
	}

	// TODO: move to DateTimeService
	/**
	 * Given a day, this function will return the start time for the first shift of the day.
	 * If given "today", it will return the first shift that is after or during
	 * the current time in seconds after midnight (the seconds are from the store's timezone/perspective).
	 */
	get startShift() {
		return (day: Dayjs, locationId: string | null = null): IShift => {
			const locationHours = this.locationHours(locationId);
			if(!locationHours) {
				throw new Error('No location hours set!');
			}

			let shift: IShift | null;

			const dateKey = this.dateKey(day);
			if(dateKey in locationHours.exceptions) {
				shift = this.findFirstShiftForDay(day, locationHours.exceptions[dateKey].shifts);
			} else {
				const dayKey = this.dayKey(day);
				shift = this.findFirstShiftForDay(day, locationHours.defaultDays[dayKey].shifts);
			}
			if(!shift) {
				throw new Error('No shift found!');
			}
			return shift;
		};
	}

	get findFirstShiftForDay() {
		return (day: Dayjs, shifts: IShift[]) => {
			let secondsAfterMidnight = 0;
			// If today
			if(day.isSame(this.currentLocationDateTime(), 'day')) {
				// Get the current time in seconds after midnight
				secondsAfterMidnight = this.secondsAfterMidnight();
				// First, first shift that is after or during the current time
				const currentShift = shifts.find((shift) => {
					return shift.startSeconds <= secondsAfterMidnight && shift.endSeconds >= secondsAfterMidnight;
				}) || null;
				if(currentShift) {
					return currentShift;
				}
				// Else fall through to the logic below to find the next shift
			}
			// If no current shift, return the next shift
			let earliestShift: IShift | null = null;
			for(const shift of shifts) {
				if(shift.startSeconds >= secondsAfterMidnight) {
					if(!earliestShift || shift.startSeconds < earliestShift.startSeconds) {
						earliestShift = shift;
					}
				}
			}
			return earliestShift;
		};
	}

	/**
	 * returns all shifts from given list occurring during or after current location's time
	 *
	 * TODO: convert uses to DateTimeService.locationGetRemainingShiftsForDay
	 */
	get remainingShiftsForDay() {
		return (day: Dayjs, shifts: IShift[]): IShift[] => {
			return DateTimeService.locationGetRemainingShiftsForDay(
				this.location,
				day,
				shifts
			);
		};
	}

	/**
	 * provides current time in current location's timezone as seconds after midnight
	 */
	get secondsAfterMidnight() {
		// inner function prevents caching
		return (): number => DateTimeService.convertDayjsToSeconds(this.currentLocationDateTime());
	}

	/**
	 * Day key refers to the key we use for default days which use `default-$dayOfWeek`
	 */
	get dayKey() {
		return (day: Dayjs) => {
			return `default-${day.format('d')}`;
		};
	}

	/**
	 * Date key refers to the key we use for exceptions which use `D-MM-YY`
	 * (yes, no leading 0 on the day but a leading 0 on the month)
	 */
	get dateKey() {
		return (day: Dayjs) => {
			return day.format('D-MM-YY');
		};
	}

	/**
	 * returns current datetime (as Dayjs) for selected ordering location
	 */
	get currentLocationDateTime() {
		// inner function prevents caching
		return (): Dayjs => DateTimeService.locationGetLocalNow(this.location);
	}

	// TODO: move to DateTimeService
	/**
	 * returns the start time of the next shift with available pickup time
	 *
	 * This method looks in shifts up to seven days ahead. It returns (short-circuits)
	 * with the first viable shift. The location's configured wait time is taken
	 * into consideration and included in the returned value.
	 */
	get nextAvailableLocationDateTime() {
		// inner function prevents caching
		return (): INextAvailableDate | null => {
			let date = this.currentLocationDateTime();
			let counter = 0;

			const waitTimeSeconds = (OrderStore.location?.currentWaitTime ?? 0) * 60;

			// N.B.: This block only loops if an Error is thrown before a return statement
			do {
				try {
					/**
					 * current or next shift for today, first shift for subsequent days
					 */
					const firstShiftForDate = this.startShift(date);
					const nowSecondsAfterMidnight = this.secondsAfterMidnight();
					const nowPlusWaitTime = nowSecondsAfterMidnight + waitTimeSeconds;

					/*
					 * firstShiftForDate may be the current, active shift. This
					 * should only be possible on the initial loop, when the
					 * value for `date` is today.
					 *
					 * Using <= instead of < allows the wait time to be zero.
					 */
					if(firstShiftForDate.startSeconds <= nowSecondsAfterMidnight) {
						/*
						 * If, with wait time, we're still inside the current shift,
						 * proceed. Otherwise, look at the next shift. We don't need
						 * to look at any other shifts for today. Instead, restart
						 * the do-while loop to advance to the next day.
						 */
						if(nowPlusWaitTime < firstShiftForDate.endSeconds) {
							return {
								date: date.add(waitTimeSeconds, 'seconds'),
								isInActiveShift: true,
							};
						} else {
							/*
							 * Here, we know nowPlusWaitTime is after firstShiftForDate's
							 * end, meaning the first shift returned by availability()
							 * is likely the next one. We must also check "now" without
							 * wait time.
							 *
							 * Also, currentLocationDateTime() would've already thrown
							 * an error if no location is defined.
							 */
							const shifts = this.availability(this.location?.id, 1);
							if(shifts.length > 0 &&
								shifts[0].dayId === date.format('YYYY-MM-DD') &&
								shifts[0].shifts.length > 0
							) {
								let nextShift = shifts[0].shifts[0];

								// If the nextShift is the current one, go to the next today, if available.
								if(nextShift.endSeconds <= nowSecondsAfterMidnight) {
									if(shifts[0].shifts.length > 1) {
										nextShift = shifts[0].shifts[1];
									} else {
										// skip to next loop
										throw new Error('Skipping to next date');
									}
								}

								return {
									date: DateTimeService.convertSecondsToDayjs(nextShift.startSeconds + waitTimeSeconds),
									isInActiveShift: false,
								};
							}

							// skip to next loop
							throw new Error('Skipping to next date');
						}

					/*
					 * We know that firstShiftForDate is either the current or next
					 * shift for today (in the initial loop). If the start of that
					 * shift is greater than "now" (the if-condition, below), we can
					 * infer it is the next shift.
					 *
					 * We also know that the firstShiftForDate for future dates will
					 * also satisfy this condition. In all cases, set the date to the
					 * start of the found shift and set isInActiveShift to false.
					 */
					} else if(firstShiftForDate.startSeconds > nowSecondsAfterMidnight) {
						return {
							date: date.startOf('day').add(firstShiftForDate.startSeconds + waitTimeSeconds, 'seconds'),
							isInActiveShift: false,
						};
					}
				} catch(error) {}

				counter++;
				date = date.add(1, 'day');
			} while(counter <= 6);

			return null;
		};
	}

	// TODO: move to DateTimeService
	/**
	 * returns array of shifts for the next week
	 *
	 * The returned array's indices correspond to offsets from today
	 * (i.e., today = 0, tomorrow = 1). Shifts that have already ended
	 * are not included.
	 */
	get availability() {
		return (locationId: string | null = null, numberOfDays = 7): IAvailability[] => {
			const locationHours = this.locationHours(locationId);
			if(!locationHours) {
				return [];
			}

			const availability: IAvailability[] = [];
			// I don't think this is correct. This function call uses this.location
			// which uses OrderStore.selectedLocationId which might not always be what we want.
			// We should enhance this.currentLocationDateTime to accept a locationId and then fallback
			// to OrderStore.selectedLocationId if nothing is passed in. Then update the line below
			// to pass in locationId from this function's params.
			// Issue tracked here: https://app.clickup.com/t/8688x4tzv
			const now = this.currentLocationDateTime();

			for(const index of new Array(numberOfDays).keys()) {
				const nowPlusIndex = now.add(index, 'day').startOf('day');
				const dateKey = nowPlusIndex.format('YYYY-MM-DD');
				const dayKey = this.dayKey(nowPlusIndex);

				let shifts = [];
				if(dateKey in locationHours.exceptions) {
					shifts = locationHours.exceptions[dateKey].shifts;
				} else {
					shifts = locationHours.defaultDays[dayKey].shifts;
				}

				availability.push({
					dayId: dayKey,
					date: nowPlusIndex,
					shifts:	this.remainingShiftsForDay(nowPlusIndex, shifts)
				});
			}

			return availability;
		};
	}

	get isOpen() {
		return (locationId: string | null = null): boolean => {
			if(locationId) {
				const availabilities = this.availability(locationId, 1);
				if(!availabilities.length) {
					return false;
				}
				// Check day first
				if(!dayjs().isSame(availabilities[0].date, 'day')) {
					return false;
				}
				// Check that now is between the first shift's start and end
				const now: number = DateTimeService.convertDayjsToSeconds(DateTimeService.locationGetLocalNow(LocationStore.location(locationId)));
				if(now >= availabilities[0].shifts[0].startSeconds && now <= availabilities[0].shifts[0].endSeconds) {
					return true;
				}
			}
			return false;
		};
	}

	@Action
	async fetchLocationHours(locationId: string) {
		// This request can fire right as the app starts up so it need to wait for
		// the logged in client to be ready.
		await WebService.loggedInStarted;
		if(!this.locationHoursInternal[locationId]) {
			const response = await WebService.authenticatedClient.get<IBaseResponse<{locationHours: ILocationHours}>>(`locations/${locationId}/hours`);
			this.setLocationHours({locationId: locationId, locationHours: response.data.data.locationHours});
		}
	}

	@Mutation
	public setLocationHours(params: {locationId: string; locationHours: ILocationHours}) {
		Vue.set(this.locationHoursInternal, params.locationId, params.locationHours);
	}

	@Mutation
	public clearLocationHours() {
		this.locationHoursInternal = {};
	}
}


export {
	LocationHoursModule
};
